import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/shared/material.module';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { SnackBarService } from 'src/app/shared/services/snack-service.service';
import { ClassificationPreviewComponent } from '../classification-preview/classification-preview.component';
import { mapTemplateError } from '../template-error';
import { PreviewNode, buildPreviewTree } from '../template-tree';
import { ApplyTemplateResult, WorkClassificationTemplate } from '../work-classification-template.models';
import { WorkClassificationTemplateService } from '../work-classification-template.service';

export interface ApplyTemplateDialogData {
  projectId: number;
}

/**
 * "Käytä mallia": creates the project's tree as a copy of a template. Always runs as onlyIfEmpty,
 * it can never replace an existing tree. Closes with the apply result (also when nothing was applied).
 */
@Component({
  selector: 'app-apply-template-dialog',
  templateUrl: './apply-template-dialog.component.html',
  styleUrls: ['./apply-template-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MaterialModule, TranslatePipe, ClassificationPreviewComponent],
})
export class ApplyTemplateDialogComponent implements OnInit {
  private data = inject<ApplyTemplateDialogData>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<ApplyTemplateDialogComponent, ApplyTemplateResult>>(MatDialogRef);
  private service = inject(WorkClassificationTemplateService);
  private snackBar = inject(SnackBarService);
  private translate = inject(TranslateService);
  private logger = inject(LoggerService);
  private destroyRef = inject(DestroyRef);

  templates = signal<WorkClassificationTemplate[]>([]);
  loading = signal(true);
  loadFailed = signal(false);
  selectedId = signal<number | null>(null);
  previewNodes = signal<PreviewNode[]>([]);
  saving = signal(false);

  selected = computed(() => this.templates().find(t => t.id === this.selectedId()) ?? null);

  ngOnInit(): void {
    this.loadTemplates();
  }

  loadTemplates(): void {
    this.loading.set(true);
    this.service
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: list => {
          this.templates.set(list);
          this.loading.set(false);
          // The backend lists the default first, so the first row is the preselection.
          this.select(list.find(t => t.isDefault)?.id ?? list[0]?.id ?? null);
        },
        error: err => {
          this.loading.set(false);
          this.loadFailed.set(true);
          this.logger.errorWithContext('ApplyTemplateDialog', 'Failed to load templates', err);
        },
      });
  }

  select(id: number | null): void {
    this.selectedId.set(id);
    this.previewNodes.set([]);
    if (id == null) {
      return;
    }
    this.service
      .get(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: detail => {
          if (this.selectedId() === id) {
            this.previewNodes.set(buildPreviewTree(detail.nodes));
          }
        },
        error: err => this.logger.errorWithContext('ApplyTemplateDialog', 'Failed to load template preview', err),
      });
  }

  apply(): void {
    const id = this.selectedId();
    if (id == null || this.saving()) {
      return;
    }
    this.saving.set(true);
    this.service
      .apply(id, this.data.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.saving.set(false);
          this.dialogRef.close(result);
        },
        error: err => {
          this.saving.set(false);
          const m = mapTemplateError(err);
          this.snackBar.error(this.translate.instant(m.key, m.params));
          if (m.code === 'TEMPLATE_INACTIVE' || m.code === 'TEMPLATE_EMPTY' || m.code === 'TEMPLATE_NOT_FOUND') {
            // The template was changed or removed meanwhile: refresh the choices.
            this.loadTemplates();
          }
        },
      });
  }
}
